import { useState, useMemo } from "react";

export interface DomainVariable {
  id: string;
  domain: string;
  label: string;
  description: string;
  states: { value: string; label: string; description: string }[];
}

export interface InterventionOption {
  id: string;
  label: string;
  type: "email" | "sms" | "hardware" | "software" | "human" | "report" | "alert" | "shutdown" | "evacuate";
  description: string;
}

export interface TruthTableExercise {
  id: string;
  title: string;
  description: string;
  industry: string;
  variables: DomainVariable[];
  interventions: InterventionOption[];
  correctInterventions: Record<string, string>;
  hint?: string;
}

interface TruthTableBuilderProps {
  exercise: TruthTableExercise;
  onComplete?: (score: number, passed: boolean) => void;
  onClose?: () => void;
}

const INTERVENTION_ICONS: Record<InterventionOption["type"], string> = {
  email: "\u2709",
  sms: "\u2709",
  hardware: "\u2699",
  software: "\u2699",
  human: "\u263B",
  report: "\u2630",
  alert: "\u26A0",
  shutdown: "\u23FB",
  evacuate: "\u26A1",
};

const INTERVENTION_COLORS: Record<InterventionOption["type"], string> = {
  email: "var(--primary-500)",
  sms: "var(--primary-400)",
  hardware: "var(--warning-500)",
  software: "var(--accent-500)",
  human: "var(--accent-600)",
  report: "var(--neutral-500)",
  alert: "var(--error-500)",
  shutdown: "var(--error-600)",
  evacuate: "var(--error-700)",
};

type Phase = "select" | "simulate" | "results";

export function TruthTableBuilder({ exercise, onComplete, onClose }: TruthTableBuilderProps) {
  const [phase, setPhase] = useState<Phase>("select");
  const [selectedVarIds, setSelectedVarIds] = useState<string[]>([]);
  const [assignedInterventions, setAssignedInterventions] = useState<Record<string, string>>({});
  const [currentComboIdx, setCurrentComboIdx] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState<"none" | "correct" | "wrong">("none");

  const selectedVars = exercise.variables.filter((v) => selectedVarIds.includes(v.id));

  const combinations = useMemo(() => {
    if (selectedVars.length === 0) return [];
    const result: { combo: string[]; labels: string[]; key: string }[] = [];
    function generate(idx: number, current: string[], currentLabels: string[]) {
      if (idx === selectedVars.length) {
        result.push({ combo: [...current], labels: [...currentLabels], key: current.join("|") });
        return;
      }
      for (const state of selectedVars[idx].states) {
        generate(idx + 1, [...current, state.value], [...currentLabels, state.label]);
      }
    }
    generate(0, [], []);
    return result;
  }, [selectedVars]);

  function toggleVariable(varId: string) {
    if (phase !== "select") return;
    setSelectedVarIds((prev) =>
      prev.includes(varId) ? prev.filter((v) => v !== varId) : prev.length < 3 ? [...prev, varId] : prev,
    );
    setAssignedInterventions({});
  }

  function startSimulation() {
    if (selectedVars.length < 2) return;
    setPhase("simulate");
    setCurrentComboIdx(0);
    setAssignedInterventions({});
    setFeedback("none");
  }

  function selectIntervention(interventionId: string) {
    if (phase !== "simulate" || feedback !== "none") return;
    const combo = combinations[currentComboIdx];
    if (!combo) return;
    const expected = exercise.correctInterventions[combo.key];
    setAssignedInterventions((prev) => ({ ...prev, [combo.key]: interventionId }));
    setFeedback(interventionId === expected ? "correct" : "wrong");
  }

  function nextScenario() {
    setFeedback("none");
    if (currentComboIdx + 1 >= combinations.length) {
      setPhase("results");
      let correct = 0;
      for (const combo of combinations) {
        if (exercise.correctInterventions[combo.key] === assignedInterventions[combo.key]) correct++;
      }
      const total = combinations.length;
      const score = total > 0 ? Math.round((correct / total) * 100) : 0;
      onComplete?.(score, score >= 70);
    } else {
      setCurrentComboIdx((i) => i + 1);
    }
  }

  function handleReset() {
    setPhase("select");
    setSelectedVarIds([]);
    setAssignedInterventions({});
    setCurrentComboIdx(0);
    setFeedback("none");
  }

  const currentCombo = combinations[currentComboIdx];
  const totalAssigned = Object.keys(assignedInterventions).length;

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto" }}>
      {/* Header */}
      <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)" }}>
        <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--accent-600)", marginBottom: "0.25rem" }}>
          Truth Table Decision Logic Simulation
        </div>
        <h3 style={{ fontSize: "1.125rem", marginBottom: "0.25rem" }}>{exercise.title}</h3>
        <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{exercise.description}</p>
      </div>

      {/* Progress indicator */}
      {phase === "simulate" && currentCombo && (
        <div style={{ marginBottom: "var(--space-3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.375rem" }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--text-muted)" }}>
              Scenario {currentComboIdx + 1} of {combinations.length}
            </span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{Math.round((totalAssigned / combinations.length) * 100)}% complete</span>
          </div>
          <div style={{ height: "6px", borderRadius: "3px", background: "var(--neutral-200)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((currentComboIdx) / combinations.length) * 100}%`, background: "var(--accent-500)", borderRadius: "3px", transition: "width 0.3s ease" }} />
          </div>
        </div>
      )}

      {/* Phase 1: Select Variables */}
      {phase === "select" && (
        <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
            <h4 style={{ fontSize: "0.9375rem", fontWeight: 700 }}>Step 1: Select Domain Variables (2-3)</h4>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{selectedVarIds.length} / 3 selected</span>
          </div>
          <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>
            Choose the sensor domains you want to monitor. Each variable has multiple states (e.g., Normal / Warning / Critical). The simulation will present every possible combination one at a time.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "0.5rem" }}>
            {exercise.variables.map((variable) => {
              const selected = selectedVarIds.includes(variable.id);
              const disabled = !selected && selectedVarIds.length >= 3;
              return (
                <button
                  key={variable.id}
                  onClick={() => toggleVariable(variable.id)}
                  disabled={disabled}
                  style={{
                    padding: "0.75rem",
                    borderRadius: "var(--radius-md)",
                    border: selected ? "2px solid var(--accent-500)" : "1px solid var(--border)",
                    background: selected ? "var(--accent-50)" : disabled ? "var(--neutral-100)" : "var(--surface)",
                    cursor: disabled ? "not-allowed" : "pointer",
                    textAlign: "left",
                    opacity: disabled ? 0.5 : 1,
                    transition: "all var(--transition-fast)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.25rem" }}>
                    <span style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{variable.domain}</span>
                    {selected && <span style={{ color: "var(--accent-500)", fontSize: "0.875rem" }}>&#10003;</span>}
                  </div>
                  <div style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.25rem" }}>{variable.label}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.375rem" }}>{variable.description}</div>
                  <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                    {variable.states.map((s) => (
                      <span key={s.value} style={{ fontSize: "0.625rem", padding: "0.125rem 0.375rem", borderRadius: "var(--radius-sm)", background: "var(--neutral-100)", color: "var(--text-muted)", fontWeight: 600 }}>
                        {s.label}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>

          {exercise.hint && (
            <div style={{ marginTop: "var(--space-3)" }}>
              <button onClick={() => setShowHint(!showHint)} style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "0.375rem 0.75rem", fontSize: "0.75rem", cursor: "pointer", color: "var(--primary-600)" }}>
                {showHint ? "Hide Hint" : "Show Hint"}
              </button>
              {showHint && (
                <div style={{ marginTop: "0.5rem", padding: "0.75rem", borderRadius: "var(--radius-md)", background: "var(--primary-50)", border: "1px solid var(--primary-200)", fontSize: "0.8125rem", color: "var(--primary-700)" }}>
                  {exercise.hint}
                </div>
              )}
            </div>
          )}

          {selectedVars.length >= 2 && (
            <div style={{ marginTop: "var(--space-3)", display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <button onClick={startSimulation} className="btn btn-primary" style={{ fontSize: "0.875rem" }}>
                Start Simulation ({combinations.length} scenarios)
              </button>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                You'll be presented with each scenario one at a time
              </span>
            </div>
          )}
        </div>
      )}

      {/* Phase 2: Guided Simulation */}
      {phase === "simulate" && currentCombo && (
        <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)" }}>
          <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: "0.25rem" }}>Scenario {currentComboIdx + 1}: What should Sentinel do?</h4>
          <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>
            The following sensor conditions have been detected. Select the appropriate intervention for this exact combination.
          </p>

          {/* Current scenario - sensor state cards */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${currentCombo.labels.length}, 1fr)`, gap: "0.75rem", marginBottom: "var(--space-4)" }}>
            {currentCombo.labels.map((label, li) => {
              const variable = selectedVars[li];
              const state = variable.states[li];
              const isCritical = label.toLowerCase().includes("critical") || label.toLowerCase().includes("fail") || label.toLowerCase().includes("high") || label.toLowerCase().includes("imminent") || label.toLowerCase().includes("active") || label.toLowerCase().includes("explosive") || label.toLowerCase().includes("rupture") || label.toLowerCase().includes("collapse") || label.toLowerCase().includes("breach") || label.toLowerCase().includes("leak") || label.toLowerCase().includes("contaminated") || label.toLowerCase().includes("danger") || label.toLowerCase().includes("hazard");
              const isWarning = label.toLowerCase().includes("warning") || label.toLowerCase().includes("elevated") || label.toLowerCase().includes("stress") || label.toLowerCase().includes("degraded") || label.toLowerCase().includes("drifting") || label.toLowerCase().includes("rough") || label.toLowerCase().includes("suspect") || label.toLowerCase().includes("adverse") || label.toLowerCase().includes("shifting") || label.toLowerCase().includes("congested") || label.toLowerCase().includes("reduced") || label.toLowerCase().includes("seepage") || label.toLowerCase().includes("fluctuating") || label.toLowerCase().includes("anomalous") || label.toLowerCase().includes("approaching") || label.toLowerCase().includes("declining") || label.toLowerCase().includes("caution") || label.toLowerCase().includes("disrupted") || label.toLowerCase().includes("delayed") || label.toLowerCase().includes("worn") || label.toLowerCase().includes("minor") || label.toLowerCase().includes("moderate") || label.toLowerCase().includes("emerging") || label.toLowerCase().includes("activity") || label.toLowerCase().includes("alert") || label.toLowerCase().includes("smoke") || label.toLowerCase().includes("trace") || label.toLowerCase().includes("rising") || label.toLowerCase().includes("low") || label.toLowerCase().includes("irregular");
              return (
                <div key={li} style={{
                  padding: "var(--space-3)",
                  borderRadius: "var(--radius-md)",
                  border: `2px solid ${isCritical ? "var(--error-300)" : isWarning ? "var(--warning-300)" : "var(--accent-300)"}`,
                  background: isCritical ? "var(--error-50)" : isWarning ? "var(--warning-50)" : "var(--accent-50)",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.25rem" }}>
                    {variable.domain}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                    {variable.label}
                  </div>
                  <div style={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    padding: "0.375rem 0.75rem",
                    borderRadius: "var(--radius-sm)",
                    background: isCritical ? "var(--error-100)" : isWarning ? "var(--warning-100)" : "var(--accent-100)",
                    color: isCritical ? "var(--error-700)" : isWarning ? "var(--warning-700)" : "var(--accent-700)",
                  }}>
                    {label}
                  </div>
                  {state && (
                    <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                      {state.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Intervention selection buttons */}
          <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--text)" }}>
            Select the action Sentinel should take:
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.5rem" }}>
            {exercise.interventions.map((int) => {
              const assigned = assignedInterventions[currentCombo.key];
              const isSelected = assigned === int.id;
              const expected = exercise.correctInterventions[currentCombo.key];
              const showResult = feedback !== "none";
              const isCorrect = showResult && int.id === expected;
              const isWrongSelection = showResult && isSelected && int.id !== expected;
              return (
                <button
                  key={int.id}
                  onClick={() => selectIntervention(int.id)}
                  disabled={feedback !== "none"}
                  style={{
                    padding: "0.75rem",
                    borderRadius: "var(--radius-md)",
                    border: isCorrect ? "2px solid var(--accent-500)" : isWrongSelection ? "2px solid var(--error-500)" : isSelected ? "2px solid var(--primary-500)" : "1px solid var(--border)",
                    background: isCorrect ? "var(--accent-50)" : isWrongSelection ? "var(--error-50)" : isSelected ? "var(--primary-50)" : "var(--surface)",
                    cursor: feedback !== "none" ? "default" : "pointer",
                    textAlign: "left",
                    transition: "all var(--transition-fast)",
                    opacity: showResult && !isCorrect && !isWrongSelection ? 0.4 : 1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", marginBottom: "0.25rem" }}>
                    <span style={{ fontSize: "1rem", color: INTERVENTION_COLORS[int.type] }}>{INTERVENTION_ICONS[int.type]}</span>
                    <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{int.label}</span>
                    {isCorrect && <span style={{ marginLeft: "auto", color: "var(--accent-600)", fontWeight: 700 }}>&#10003;</span>}
                    {isWrongSelection && <span style={{ marginLeft: "auto", color: "var(--error-600)", fontWeight: 700 }}>&#10007;</span>}
                  </div>
                  <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>{int.description}</div>
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {feedback !== "none" && (
            <div style={{
              marginTop: "var(--space-3)",
              padding: "var(--space-3)",
              borderRadius: "var(--radius-md)",
              background: feedback === "correct" ? "var(--accent-50)" : "var(--error-50)",
              border: `1px solid ${feedback === "correct" ? "var(--accent-300)" : "var(--error-300)"}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <span style={{ fontSize: "1.25rem" }}>{feedback === "correct" ? "\u2705" : "\u26A0"}</span>
                <span style={{ fontWeight: 700, color: feedback === "correct" ? "var(--accent-700)" : "var(--error-700)" }}>
                  {feedback === "correct" ? "Correct intervention!" : "Not the recommended action"}
                </span>
              </div>
              {feedback === "wrong" && (
                <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", margin: 0 }}>
                  The recommended action for this scenario is: <strong>{exercise.interventions.find((i) => i.id === exercise.correctInterventions[currentCombo.key])?.label ?? "N/A"}</strong>
                </p>
              )}
              <button onClick={nextScenario} className="btn btn-primary" style={{ marginTop: "0.75rem", fontSize: "0.8125rem" }}>
                {currentComboIdx + 1 >= combinations.length ? "View Results" : "Next Scenario \u2192"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Phase 3: Results */}
      {phase === "results" && (
        <div className="card" style={{ padding: "var(--space-4)" }}>
          {(() => {
            let correct = 0;
            for (const combo of combinations) {
              if (exercise.correctInterventions[combo.key] === assignedInterventions[combo.key]) correct++;
            }
            const total = combinations.length;
            const score = Math.round((correct / total) * 100);
            const passed = score >= 70;
            return (
              <>
                <div style={{ textAlign: "center", marginBottom: "var(--space-4)" }}>
                  <div style={{ fontSize: "2.5rem", marginBottom: "0.25rem" }}>{passed ? "\u2705" : "\u26A0"}</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 800, color: passed ? "var(--accent-600)" : "var(--warning-600)" }}>{score}%</div>
                  <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>
                    {correct} of {total} correct interventions
                  </p>
                </div>

                {/* Summary table */}
                <div style={{ overflowX: "auto", marginBottom: "var(--space-3)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid var(--border)" }}>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>#</th>
                        {selectedVars.map((v) => (
                          <th key={v.id} style={{ padding: "0.5rem 0.75rem", textAlign: "left", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>
                            {v.domain}
                          </th>
                        ))}
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Your Action</th>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Expected</th>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "center", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinations.map((combo, ci) => {
                        const assigned = assignedInterventions[combo.key];
                        const expected = exercise.correctInterventions[combo.key];
                        const isCorrect = assigned === expected;
                        return (
                          <tr key={combo.key} style={{ borderBottom: "1px solid var(--border)", background: isCorrect ? "var(--accent-50)" : "var(--error-50)" }}>
                            <td style={{ padding: "0.5rem 0.75rem", fontWeight: 600 }}>{ci + 1}</td>
                            {combo.labels.map((label, li) => (
                              <td key={li} style={{ padding: "0.5rem 0.75rem", fontSize: "0.75rem", fontWeight: 600 }}>{label}</td>
                            ))}
                            <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.75rem" }}>
                              {exercise.interventions.find((i) => i.id === assigned)?.label ?? "Not answered"}
                            </td>
                            <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.75rem" }}>
                              {exercise.interventions.find((i) => i.id === expected)?.label ?? "N/A"}
                            </td>
                            <td style={{ padding: "0.5rem 0.75rem", textAlign: "center" }}>
                              {isCorrect ? <span style={{ color: "var(--accent-600)", fontWeight: 700 }}>&#10003;</span> : <span style={{ color: "var(--error-600)", fontWeight: 700 }}>&#10007;</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button onClick={handleReset} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>Start Over</button>
                  <button onClick={onClose} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Continue</button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Intervention Legend - always visible */}
      {phase !== "results" && selectedVars.length >= 2 && (
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.5rem" }}>Available Interventions</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.375rem" }}>
            {exercise.interventions.map((int) => (
              <div key={int.id} style={{ display: "flex", alignItems: "center", gap: "0.375rem", padding: "0.375rem 0.5rem", borderRadius: "var(--radius-sm)", background: "var(--bg-subtle)" }}>
                <span style={{ fontSize: "0.875rem", color: INTERVENTION_COLORS[int.type] }}>{INTERVENTION_ICONS[int.type]}</span>
                <div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 600 }}>{int.label}</div>
                  <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>{int.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
